/**
 * The enterprise shell: rail, header, mobile drawer, scroll-spy, seat meter
 * and the command palette.
 *
 * ── WHAT IS PORTED, AND WHY IT IS PORTED RATHER THAN REWRITTEN ───────────
 * The rail geometry, the maroon gradient, the crimson-gradient active pill,
 * the drawer and the scroll-spy come from p3-web's admin shell. They are
 * correct, they are what makes this read as the third sibling in a family
 * rather than a new product, and rebuilding them would be pure cost.
 *
 * Sep 9 2026, at Thomas's direction, the chrome was brought back to the
 * member app's /admin, MEASURED in a browser at 1431x782 rather than read
 * from its source: the real P3 lockup in the rail (the same artwork the
 * marketing site and the Data Matrix use, self-hosted under /brand), the
 * campus photograph at 0.12 over #efece7 behind the page, a header that is
 * just the sample pill and Export PDF at 4rem plus a hairline, and a content
 * column capped at 1520px with the member app's own padding. The header's
 * institution identity, window and comparison controls, timestamp, palette
 * button and theme toggle moved: identity and timestamp into the hero, the
 * controls into the filter bar, the palette to ⌘K, and the theme to the
 * operating system, which is what every other page on this host already does.
 *
 * The one piece worth naming: THE VIEWPORT-HEIGHT DENSITY RAMP. Nav item
 * height and font size step down at max-height 880 / 780 / 690 / 610px, gated
 * to >=1024px where the rail is fixed. Vertical space is the constraint on a
 * 13-inch laptop, not horizontal, and without the ramp the rail scrolls
 * internally, which reads as a bug. The tokens are already in tokens.css; this
 * file consumes them.
 *
 * ── TWO DELIBERATE DIVERGENCES FROM THE MEMBER APP SHELL ─────────────────
 *   Cards are opaque, not frosted. Translucency over the watermark makes an
 *     11px label's contrast unpredictable and prints as muddy grey.
 *   The seat meter sits above the profile chip, so billing state is visible
 *     without a billing visit.
 *
 * ── WHY THERE IS A <style> BLOCK IN A COMPONENT FILE ─────────────────────
 * base.css's print rules already NAME the hooks the shell must provide
 * (.e-rail, .e-topbar, .e-backdrop, .e-main, .e-asof, .e-watermark, .e-skip)
 * but do not define them, because they are shell-owned by contract. They are
 * defined here, next to the markup that uses them, and every value is a token
 * except the member app's own measured pixels, which are cited where they
 * appear. The chart hooks the panels draw into live here too, so the surface
 * has exactly one supplementary stylesheet rather than one per component.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

import { SrOnly, buttonClass } from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { navigate } from "~/app/router";
import {
  LogOutIcon,
  ADMIN_NAV,
  INVITATIONS_PAGE,
  SECTION_QUESTION,
  SECTION_TITLE,
  type AdminSectionId,
} from "~/app/lib/admin-nav";
import { formatCount, formatPercent } from "~/app/lib/format";
import type { Institution, SeatMeter } from "~/app/lib/demo-data";
import { refreshSnapshot, switchInstitution } from "~/app/lib/api";
import { clearLiveCache } from "~/app/lib/live-cache";
import { initialsOf, logout, roleLabel, type Viewer } from "~/app/lib/session";

/* ==========================================================================
   Scroll-spy

   Ported unchanged from p3-web, 55 lines, deterministic: the last section
   whose top has passed the trigger line wins. The ramp in the final viewport
   of scroll exists because a trailing short section can never reach the normal
   trigger, since the page bottoms out first, and without it the last nav item
   never lights up.
   ========================================================================== */

const TRIGGER = 140;

function useScrollSpy(sectionIds: readonly string[], enabled: boolean): string {
  const [active, setActive] = useState<string>(sectionIds[0] ?? "");
  const idsKey = sectionIds.join("|");

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    const ids = idsKey.split("|").filter(Boolean);

    function recompute() {
      const vh = window.innerHeight;
      const maxScroll = document.documentElement.scrollHeight - vh;
      const remaining = Math.max(0, maxScroll - window.scrollY);
      const trigger =
        remaining >= vh ? TRIGGER : TRIGGER + (vh - TRIGGER) * (1 - remaining / vh);
      let current = ids[0] ?? "";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - trigger <= 0) current = id;
      }
      setActive(current);
    }

    recompute();
    window.addEventListener("scroll", recompute, { passive: true });
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, [enabled, idsKey]);

  return active;
}


/* ==========================================================================
   Small icons used by the chrome only. Nav icons live in admin-nav.ts.
   ========================================================================== */

function Glyph({ d, className }: { d: string; className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={d} />
    </svg>
  );
}

const MENU_D = "M4 7h16M4 12h16M4 17h16";
const SEARCH_D = "M10.8 17.6a6.8 6.8 0 1 0 0-13.6 6.8 6.8 0 0 0 0 13.6ZM20 20l-4.4-4.4";

/**
 * The P3 lockup, the same artwork the marketing site's nav and the member
 * app's rail render from the Webflow CDN, served from this host so a paying
 * institution's dashboard chrome does not depend on the marketing site's
 * asset host. 946.6 by 292.6, which is the 3.235 ratio the member app's rail
 * measures at 109.99 by 34.
 */
const P3_LOGO = "/brand/p3-logo-white.svg";

/* ==========================================================================
   Command palette

   Scoped to what a menu cannot do. A palette that mirrors the nav is
   decoration; this one switches the window, toggles the comparison, copies the
   join link, jumps to a section by the QUESTION it answers, and changes the
   demonstration state. Every entry is also reachable by mouse, which is both a
   usability rule and a WCAG 2.1 AA requirement.
   ========================================================================== */

export interface PaletteCommand {
  readonly id: string;
  readonly group: string;
  readonly label: string;
  /** Right-aligned context: the current value of a setting, or an answer. */
  readonly hint?: string;
  /** Extra words the matcher should see but the reader should not. */
  readonly keywords?: string;
  readonly selected?: boolean;
  readonly run: () => void;
}

function matches(command: PaletteCommand, query: string): boolean {
  if (!query) return true;
  const hay = `${command.label} ${command.hint ?? ""} ${command.keywords ?? ""} ${command.group}`.toLowerCase();
  return query
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

function CommandPalette({
  commands,
  onClose,
}: {
  commands: readonly PaletteCommand[];
  onClose: () => void;
}) {
  const [query, setQuery] = useState("");
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const baseId = useId();

  const results = useMemo(
    () => commands.filter((c) => matches(c, query)),
    [commands, query],
  );

  useEffect(() => setCursor(0), [query]);

  useEffect(() => {
    inputRef.current?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>('[data-cursor="true"]');
    el?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    if (event.key === "ArrowDown" || (event.key === "n" && event.ctrlKey)) {
      event.preventDefault();
      setCursor((c) => (results.length === 0 ? 0 : (c + 1) % results.length));
      return;
    }
    if (event.key === "ArrowUp" || (event.key === "p" && event.ctrlKey)) {
      event.preventDefault();
      setCursor((c) =>
        results.length === 0 ? 0 : (c - 1 + results.length) % results.length,
      );
      return;
    }
    if (event.key === "Home") {
      event.preventDefault();
      setCursor(0);
      return;
    }
    if (event.key === "End") {
      event.preventDefault();
      setCursor(Math.max(0, results.length - 1));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const chosen = results[cursor];
      if (chosen) {
        chosen.run();
        onClose();
      }
    }
  }

  // Group headings render inline, so the list stays one flat listbox and the
  // arrow keys never land on a heading.
  let lastGroup = "";

  const body = (
    <div className="e-cmdk-layer">
      <button
        type="button"
        className="e-cmdk-backdrop"
        aria-label="Close the command palette"
        onClick={onClose}
      />
      <div
        className="e-cmdk"
        role="dialog"
        aria-modal="true"
        aria-label="Commands"
        onKeyDown={onKeyDown}
      >
        <div className="e-cmdk-search">
          <Glyph d={SEARCH_D} className="e-cmdk-search-icon" />
          <input
            ref={inputRef}
            className="e-cmdk-input"
            type="text"
            value={query}
            placeholder="Jump to a section, change the window, copy the join link"
            onChange={(e) => setQuery(e.target.value)}
            role="combobox"
            aria-expanded
            aria-controls={`${baseId}-list`}
            aria-activedescendant={
              results[cursor] ? `${baseId}-opt-${results[cursor]?.id}` : undefined
            }
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="e-kbd">Esc</kbd>
        </div>

        {results.length === 0 ? (
          <p className="e-cmdk-empty">
            Nothing matches that. Try a section name, a window, or "copy".
          </p>
        ) : (
          <ul className="e-cmdk-list" id={`${baseId}-list`} role="listbox" ref={listRef}>
            {results.map((command, i) => {
              const heading = command.group !== lastGroup ? command.group : null;
              lastGroup = command.group;
              const isCursor = i === cursor;
              return (
                <li key={command.id} className="e-cmdk-li">
                  {heading ? <p className="e-cmdk-group e-label">{heading}</p> : null}
                  <button
                    type="button"
                    id={`${baseId}-opt-${command.id}`}
                    role="option"
                    aria-selected={isCursor}
                    data-cursor={isCursor}
                    className={cn("e-cmdk-item", isCursor && "is-cursor")}
                    onMouseMove={() => setCursor(i)}
                    onClick={() => {
                      command.run();
                      onClose();
                    }}
                  >
                    <span className="e-cmdk-label">
                      {command.selected ? (
                        <span className="e-cmdk-tick" aria-hidden="true">
                          {"✓"}
                        </span>
                      ) : (
                        <span className="e-cmdk-tick" aria-hidden="true" />
                      )}
                      {command.label}
                      {command.selected ? <SrOnly>, current</SrOnly> : null}
                    </span>
                    {command.hint ? (
                      <span className="e-cmdk-hint">{command.hint}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <div className="e-cmdk-foot">
          <span>
            <kbd className="e-kbd">{"↑"}</kbd>
            <kbd className="e-kbd">{"↓"}</kbd> to move
          </span>
          <span>
            <kbd className="e-kbd">Enter</kbd> to run
          </span>
          <span>Every command here is also a control on the page.</span>
        </div>
      </div>
    </div>
  );

  if (typeof document === "undefined") return null;
  return createPortal(body, document.body);
}

/* ==========================================================================
   The institution switch (Sep 10 2026)

   Shown only to a person who administers more than one institution, above
   the rail footer. The Worker's auth/switch issues a new session on the
   chosen membership and rotates the cookie; the page then reloads, so every
   screen reads the new institution from the session rather than patching
   state in place.
   ========================================================================== */

function InstitutionSwitch({ viewer }: { viewer: Viewer }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const memberships = viewer.memberships ?? [];

  async function choose(membershipId: string) {
    if (membershipId === viewer.membershipId) return;
    setBusy(true);
    setFailed(null);
    const result = await switchInstitution(membershipId);
    if (result.ok) {
      window.location.assign("/admin");
      return;
    }
    setBusy(false);
    setFailed(result.error.message);
  }

  return (
    <div className="e-rail-switch">
      <label className="e-rail-switch-label" htmlFor="e-rail-switch">
        Institution
      </label>
      <select
        id="e-rail-switch"
        className="e-rail-switch-select"
        value={viewer.membershipId}
        disabled={busy}
        onChange={(event) => void choose(event.target.value)}
      >
        {memberships.map((m) => (
          <option key={m.membershipId} value={m.membershipId}>
            {m.orgName}
          </option>
        ))}
      </select>
      {failed ? (
        <p className="e-rail-switch-note" role="status">
          {failed}
        </p>
      ) : null}
    </div>
  );
}

/* ==========================================================================
   The seat meter

   A seat is a contractual quantity the customer is invoiced on, so it is shown
   to an Owner or an Admin as a billing figure and hidden from a Viewer, who
   never sees an unsuppressed figure of any kind. It never blocks a student and
   there is never a message telling a partner they are underusing their plan.
   ========================================================================== */

function SeatStrip({ seat }: { seat: SeatMeter }) {
  const pct = Math.round(seat.fraction * 100);
  const tone = seat.overCapacity ? "crit" : seat.fraction >= 0.9 ? "warn" : "ok";
  const claimedText =
    seat.claimed.kind === "value" ? formatCount(seat.claimed.value) : "Not shown";
  return (
    <div className="e-seat">
      <p className="e-seat-head">
        <span className="e-label">Capacity</span>
        <span className="e-seat-figure">
          {claimedText} of {formatCount(seat.purchased)}
        </span>
      </p>
      <div
        className={cn("e-seat-track", `is-${tone}`)}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={seat.purchased}
        aria-valuenow={seat.claimedRaw}
        aria-label={`${formatCount(seat.claimedRaw)} students claimed of a ${formatCount(seat.purchased)} student band`}
      >
        <i style={{ width: `${Math.max(pct, seat.claimedRaw > 0 ? 2 : 0)}%` }} />
      </div>
      <p className="e-seat-note">
        {seat.overCapacity
          ? "Past your band. Nothing is blocked for your students."
          : `${formatPercent(seat.fraction)} seats used`}
      </p>
    </div>
  );
}

/* ==========================================================================
   The shell
   ========================================================================== */

/** Where the shell's routes hang: the signed-in dashboard or the sample one. */
export type AdminBasePath = "/admin" | "/demo";

/** Which screen the shell is wrapping. The rail's section links scroll on the
 *  dashboard and navigate back to it from anywhere else. */
export type AdminPage = "dashboard" | "settings" | "billing" | "team" | "enrollment" | "invitations" | "matching";

/** The Export PDF control, /admin's, shared by every screen in the shell. */
export function ExportPdfButton() {
  return (
    <button type="button" className="e-export-btn" onClick={() => window.print()}>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <path d="m7 10 5 5 5-5" />
        <path d="M12 15V3" />
      </svg>
      Export PDF
    </button>
  );
}

/**
 * Recompute, beside Export PDF on every screen (Thomas, Sep 17 2026).
 *
 * The control existed only at the bottom of Settings, which is a long way to
 * go for the one action an owner takes while watching something change: the
 * hourly clock is what moves a rope from waiting to mentoring, and during the
 * matching rehearsal it was pressed by hand each time.
 *
 * It is the OWNER's, because the route is, and it is never on the sample host,
 * where there is nothing to recompute. The pull takes a few seconds, so the
 * button says which of the three things is true rather than going quiet: it
 * spins while the Worker is reading, it shows a check when the figures landed,
 * and then it reloads, because every figure on the page it sits in is now a
 * stale copy. A refusal is the Worker's own sentence under the button, and the
 * commonest one is not an error at all: figures recomputed inside the last ten
 * minutes are refused, by design.
 */
/** m:ss, for a wait that is always under ten minutes. */
function countdown(seconds: number): string {
  const whole = Math.max(0, Math.ceil(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** Where a partial recompute leaves its sentence for the page it reloads
 *  into (Sep 19 2026). Session storage only: a convenience, never state. */
const RECOMPUTE_NOTE = "p3e-recompute-note";

function RecomputeButton() {
  const [state, setState] = useState<"idle" | "busy" | "done" | "partial">("idle");
  const [problem, setProblem] = useState<string | null>(null);
  /** The Worker's sentence for a run that landed only in part. Not a
   *  refusal and not a success, so it has its own tone (the Sep 17 rule: a
   *  refusal and a success never share a slot's styling). */
  const [partial, setPartial] = useState<string | null>(null);
  /** When the Worker would allow the next one, in epoch ms. Never a guess:
   *  it comes from the refusal's own `retryAfter`, or from the ten minutes
   *  that begin at the moment a recompute succeeded. */
  const [readyAt, setReadyAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const timers = useRef<number[]>([]);

  // A tab closed mid-pull must not come back to set state on nothing.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const id of pending) window.clearTimeout(id);
    };
  }, []);

  // The sentence a partial recompute left before its reload, shown once.
  useEffect(() => {
    let note: string | null = null;
    try {
      note = window.sessionStorage.getItem(RECOMPUTE_NOTE);
      window.sessionStorage.removeItem(RECOMPUTE_NOTE);
    } catch {
      note = null;
    }
    if (!note) return;
    setPartial(note);
    const id = window.setTimeout(() => setPartial(null), 12000);
    return () => window.clearTimeout(id);
  }, []);

  // One second's tick, and only while something is counting down.
  useEffect(() => {
    if (readyAt === null) return;
    if (readyAt <= Date.now()) {
      setReadyAt(null);
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [readyAt, now]);

  const waiting = readyAt !== null && readyAt > now;

  const run = async () => {
    if (state !== "idle" || waiting) return;
    setProblem(null);
    setState("busy");
    const result = await refreshSnapshot();
    if (!result.ok) {
      setState("idle");
      setProblem(result.error.message);
      if (typeof result.error.retryAfter === "number") {
        setNow(Date.now());
        setReadyAt(Date.now() + result.error.retryAfter * 1000);
      }
      timers.current.push(window.setTimeout(() => setProblem(null), 8000));
      return;
    }
    const notice = result.value.notice;
    if (notice) {
      try {
        window.sessionStorage.setItem(RECOMPUTE_NOTE, notice);
      } catch {
        // No storage: the reload still shows the new figures.
      }
    }
    setState(notice ? "partial" : "done");
    timers.current.push(
      window.setTimeout(() => {
        // The figures on this page were read before the recompute; nothing
        // this tab is holding is current any more.
        clearLiveCache();
        window.location.reload();
      }, 1200),
    );
  };

  const label = waiting
    ? `Recompute in ${countdown((readyAt - now) / 1000)}`
    : state === "busy"
      ? "Recomputing"
      : state === "done"
        ? "Recomputed"
        : state === "partial"
          ? "Partly recomputed"
          : "Recompute";
  return (
    <span className="e-recompute">
      <button
        type="button"
        className={cn("e-export-btn", state !== "idle" && `is-${state}`, waiting && "is-waiting")}
        onClick={() => void run()}
        disabled={state !== "idle" || waiting}
        title={waiting ? "Your figures can be recomputed once every ten minutes" : "Recompute this institution's figures now"}
      >
        {state === "done" ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M20 6 9 17l-5-5" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v5h-5" />
          </svg>
        )}
        {label}
      </button>
      {/* One live region for all three, so a screen reader is told the
          outcome once rather than on every frame of the spin. */}
      <SrOnly aria-live="polite">
        {state === "busy"
          ? "Recomputing your figures."
          : state === "done"
            ? "Your figures were recomputed."
            : state === "partial"
              ? "Your headline figures were recomputed; part of the refresh did not land."
              : (problem ?? partial ?? "")}
      </SrOnly>
      {problem ? (
        <span className="e-recompute-problem" role="status">
          {problem}
        </span>
      ) : partial ? (
        <span className="e-recompute-problem is-partial" role="status">
          {partial}
        </span>
      ) : null}
    </span>
  );
}

const ROLE_RANK = { viewer: 0, admin: 1, owner: 2 } as const;
function roleAdmits(role: keyof typeof ROLE_RANK, minimum: keyof typeof ROLE_RANK): boolean {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

export interface AdminShellProps {
  institution: Institution;
  basePath: AdminBasePath;
  page: AdminPage;
  /** True while the surface is rendering fixtures. Conditional on purpose: an
   *  unconditional badge mislabels a paying customer's real numbers the day
   *  real data lands, which is the same failure in the other direction. */
  isSample: boolean;
  /** The sections actually on the page. The rail dims what is not rendered
   *  rather than offering a link that scrolls nowhere. */
  sections: readonly AdminSectionId[];
  /** Why the sections are not on the page when the reason is not the floor
   *  (Sep 19 2026): a cohort or a slice the Worker would not serve. The rail
   *  says the floor's sentence otherwise. */
  dimmedReason?: (label: string) => string;
  seat?: SeatMeter;
  /** The signed-in person (Sep 9 2026). On /admin the rail footer names
   *  them and their role; on /demo, where nobody is signed in, it names the
   *  sample institution as before. Since Sep 10 2026 the figures on /admin
   *  are the institution's own (live-view.ts), and `isSample` is false there. */
  viewer?: Viewer;
  /** Export sits at the far right of the header, after the sample pill. */
  headerActions?: ReactNode;
  commands: readonly PaletteCommand[];
  children: ReactNode;
}

export function AdminShell({
  institution,
  basePath,
  page,
  isSample,
  sections,
  dimmedReason,
  seat,
  viewer,
  headerActions,
  commands,
  children,
}: AdminShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const onDashboard = page === "dashboard";
  const active = useScrollSpy(sections, onDashboard);
  // The demonstration cohort travels with every in-shell link, so Settings
  // shows the institution the dashboard was showing.
  const search = typeof window === "undefined" ? "" : window.location.search;

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const cmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k";
      if (cmdK) {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
      if (event.key === "Escape") setNavOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const goto = useCallback(
    (id: AdminSectionId) => {
      setNavOpen(false);
      if (!onDashboard) {
        // From another screen the section is on the dashboard: go there, and
        // the router's scroll restoration lands on the hash.
        navigate(`${basePath}${search}#${id}`);
        return;
      }
      const el = document.getElementById(id);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Focus follows the scroll so a keyboard reader lands in the section, not
      // back at the top of the document.
      el.setAttribute("tabindex", "-1");
      el.focus({ preventScroll: true });
    },
    [basePath, onDashboard, search],
  );

  const sectionCommands: PaletteCommand[] = sections.map((id) => ({
    id: `goto-${id}`,
    group: "Go to",
    label: SECTION_TITLE[id],
    hint: SECTION_QUESTION[id],
    keywords: "jump scroll section",
    run: () => goto(id),
  }));

  // The screens off the scrolling page, by name. Invitations was a section
  // until Sep 17 2026 and the palette reached the join link through it; now
  // it is a route like Enrollment, Team, Billing and Settings, and the
  // palette reaches every one the rail offers this person. The screen the
  // shell is wrapping is not offered to itself.
  const routeCommands: PaletteCommand[] = ADMIN_NAV.flatMap((group) =>
    group.items.flatMap((item) => {
      if (!item.to || item.planned) return [];
      if (item.minRole && viewer && !roleAdmits(viewer.role, item.minRole)) return [];
      if (item.to === `/${page}`) return [];
      const to = item.to;
      return [
        {
          id: `open-${to.slice(1)}`,
          group: "Go to",
          label: item.label,
          hint: to === "/invitations" ? INVITATIONS_PAGE.question : undefined,
          keywords: "open screen page",
          run: () => {
            setNavOpen(false);
            navigate(`${basePath}${to}${search}`);
          },
        },
      ];
    }),
  );

  const allCommands = [...sectionCommands, ...routeCommands, ...commands];

  return (
    <div className="e-shell" data-surface="enterprise">
      <style>{SHELL_CSS}</style>

      {/* The campus photograph, the member app's own file at the member app's
          own 0.12 over #efece7, and off in dark. It carries the family
          resemblance without eating the contrast an 11px label needs. */}
      <div className="e-watermark" aria-hidden="true" />

      <a className="e-skip" href="#main">
        Skip to the dashboard
      </a>

      <aside className={cn("e-rail", navOpen && "is-open")} aria-label="Sections">
        <div className="e-rail-logo">
          <img
            className="e-rail-mark"
            src={P3_LOGO}
            alt="P3, Pulse of Perseverance"
            width={946.6}
            height={292.6}
            decoding="async"
          />
        </div>

        <nav className="e-rail-nav" aria-label="Dashboard sections">
          {ADMIN_NAV.map((group, index) => (
            <div className="e-rail-group" key={group.group ?? `g${index}`}>
              {group.group ? (
                <p className="e-rail-grouplabel">{group.group}</p>
              ) : null}
              {group.items.map((item) => {
                const present = item.section ? sections.includes(item.section) : false;
                if (item.section && present) {
                  const isActive = onDashboard && active === item.section;
                  return (
                    <a
                      key={item.label}
                      href={onDashboard ? `#${item.section}` : `${basePath}${search}#${item.section}`}
                      className={cn("e-rail-item", isActive && "is-active")}
                      aria-current={isActive ? "true" : undefined}
                      onClick={(event) => {
                        if (!onDashboard) return; // a real navigation, intercepted by the router
                        event.preventDefault();
                        if (item.section) goto(item.section);
                      }}
                    >
                      <item.icon className="e-rail-icon" />
                      <span>{item.label}</span>
                    </a>
                  );
                }
                // A route the signed-in person's role does not admit is not
                // offered (Sep 17 2026: the roster is admin and owner only).
                // The sample host has no viewer and shows everything.
                if (item.minRole && viewer && !roleAdmits(viewer.role, item.minRole)) return null;
                // A built route: Settings today. Active when it is the screen
                // the shell is wrapping.
                if (item.to && !item.planned) {
                  // Billing has no rail item since Sep 18 2026 and is reached
                  // from Settings, so Settings stays marked while it is open.
                  const isActive = item.to === `/${page}` || (page === "billing" && item.to === "/settings");
                  return (
                    <a
                      key={item.label}
                      href={`${basePath}${item.to}${search}`}
                      className={cn("e-rail-item", isActive && "is-active")}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => setNavOpen(false)}
                    >
                      <item.icon className="e-rail-icon" />
                      <span>{item.label}</span>
                    </a>
                  );
                }
                // Two different absences, and they must not look the same.
                //
                // A ROUTE that is specified but not built carries a Planned
                // chip and is not a link, because a nav item that navigates
                // nowhere is worse than one that admits it is coming.
                //
                // A SECTION that exists but is not on the page today is a
                // different thing: the cohort is under the reporting
                // threshold, nothing is unbuilt, and the page itself lists
                // exactly which sections turn on and when. So it is dimmed and
                // inert, with the reason on hover and in its accessible name,
                // and it carries no chip: a "Planned" badge on a section that
                // is finished would be a false statement about the product.
                const reason = item.section
                  ? (dimmedReason?.(item.label) ?? `${item.label} turns on when 10 students have claimed your institution.`)
                  : `${item.label} is not built yet.`;
                return (
                  <span
                    key={item.label}
                    className="e-rail-item is-planned"
                    aria-disabled="true"
                    title={reason}
                  >
                    <item.icon className="e-rail-icon" />
                    <span>{item.label}</span>
                    <SrOnly>{reason}</SrOnly>
                    {item.planned ? (
                      <span className="e-rail-planned">Planned</span>
                    ) : null}
                  </span>
                );
              })}
            </div>
          ))}
        </nav>

        {seat && seat.visibleToViewer ? <SeatStrip seat={seat} /> : null}

        {viewer?.memberships && viewer.memberships.length > 1 ? (
          <InstitutionSwitch viewer={viewer} />
        ) : null}

        <div className="e-rail-foot">
          <span className="e-rail-avatar" aria-hidden="true">
            {viewer
              ? initialsOf(viewer.name, viewer.email)
              : institution.displayName.slice(0, 2).toUpperCase()}
          </span>
          <div className="e-rail-who">
            {/* The member app's footer names the person, then their access.
                With a session that is who signed in; the sample host has no
                one signed in and names the institution instead. */}
            <p className="e-rail-org" title={viewer?.email}>
              {viewer ? (viewer.name ?? viewer.email) : institution.legalName}
            </p>
            <p className="e-rail-role">
              {viewer ? roleLabel(viewer.role) : institution.viewerRole} access
            </p>
          </div>
          {/* Log out, where the member app keeps its sign-out: a 30px button
              flush right in the footer, an 18px glyph at 55% white (measured
              Sep 9 2026). It leaves the dashboard for the Enterprise overview;
              on the signed-in host it ends the session first. */}
          <a
            className="e-rail-signout"
            href="/overview"
            aria-label="Log out"
            title="Log out"
            onClick={(event) => {
              if (basePath !== "/admin") return; // the sample host has no session to end
              event.preventDefault();
              void logout().finally(() => {
                // Nothing this tab learned outlives the session that fetched it.
                clearLiveCache();
                navigate("/overview");
              });
            }}
          >
            <LogOutIcon className="e-rail-signout-glyph" />
          </a>
        </div>
      </aside>

      {navOpen ? (
        <button
          type="button"
          className="e-backdrop"
          aria-label="Close the section menu"
          onClick={() => setNavOpen(false)}
        />
      ) : null}

      <div className="e-frame">
        <header className="e-topbar">
          <div className="e-topbar-inner">
            <button
              type="button"
              className="e-iconbtn e-only-narrow"
              aria-label="Open the section menu"
              aria-expanded={navOpen}
              onClick={() => setNavOpen(true)}
            >
              <Glyph d={MENU_D} className="e-glyph" />
            </button>

            <div className="e-topbar-right">
              {isSample ? <span className="e-sample-pill">Sample data</span> : null}
              {/* The owner's recompute, on every screen since Sep 17 2026
                  (Thomas): the hourly clock is what moves a figure, and the
                  only control for it was at the bottom of Settings. Never on
                  the sample host, and never for an admin or a viewer, because
                  the route is the owner's. */}
              {!isSample && viewer && roleAdmits(viewer.role, "owner") ? <RecomputeButton /> : null}
              {/* On the sample the header's action is the way out to register
                  (Thomas, Sep 18 2026; "See pricing" for an hour first): /demo
                  is a conversion surface, an
                  export of sample figures serves nobody, and the button that
                  used to sit under the filters was easy to scroll past. Every
                  screen passes the Export button as its action, so this one
                  substitution covers all seven; a signed-in tenant keeps its
                  own actions untouched. The primary crimson, so it reads as
                  the one thing to press. */}
              {isSample ? (
                <a className={buttonClass("primary", "sm", "e-pricing-cta")} href="/checkout">
                  Register now
                </a>
              ) : (
                headerActions
              )}
            </div>
          </div>
        </header>

        <main className={cn("e-main", page !== "dashboard" && "is-page")} id="main">
          {children}
        </main>
      </div>

      {paletteOpen ? (
        <CommandPalette commands={allCommands} onClose={() => setPaletteOpen(false)} />
      ) : null}
    </div>
  );
}

/* ==========================================================================
   Shell-owned CSS

   These are the hooks base.css's print block names but does not define, plus
   the chart and workflow hooks the panels draw into. Every value is a token
   or a cited measurement of the member app's /admin.
   ========================================================================== */

const SHELL_CSS = `
/* The shell paints nothing itself: the fixed watermark layer beneath it is
   the ground, at the member app's #efece7 (--canvas-admin), so the canvas
   token for everything inside the shell is re-pointed at that value. In dark
   the photo is off and the layer is the dark canvas. */
.e-shell {
  min-height: 100vh; color: var(--fg); background: transparent;
  --canvas: var(--canvas-admin);
  --watermark-opacity: var(--watermark-admin);
}

/* ---- Watermark ---------------------------------------------------------- */
.e-shell .e-watermark {
  position: fixed; inset: 0; z-index: -1; pointer-events: none;
  opacity: 1; background: var(--canvas);
}
.e-shell .e-watermark::after {
  content: ""; position: absolute; inset: 0;
  background: url("/img/campus-bg.jpg") center / cover no-repeat;
  opacity: var(--watermark-opacity);
}

/* ---- Rail --------------------------------------------------------------- */
.e-rail {
  position: fixed; inset-block: 0; left: 0; z-index: var(--z-rail);
  width: var(--rail-w);
  display: flex; flex-direction: column;
  background: var(--rail-bg); box-shadow: var(--e-3);
  color: var(--fg-on-dark);
  /* rem, like the member app's px-3: 11.25px at the 15px root, so the lockup
     lands at x=24.4 on both surfaces. */
  padding: var(--nav-aside-pt) 0.75rem 0.75rem;
  transform: translateX(-100%);
  transition: transform var(--dur-3) var(--ease-move);
}
.e-rail.is-open { transform: translateX(0); }
@media (min-width: 1024px) { .e-rail { transform: translateX(0); } }

/* The lockup at the member app's rail height: 34px base, stepping down with
   the density ramp, at x=24.4 from the viewport. */
.e-rail-logo { display: flex; align-items: center; padding-inline: 0.875rem;
               margin-bottom: 0.5rem; }
.e-rail-mark { height: var(--nav-logo-h); width: auto; display: block;
               align-self: flex-start; object-fit: contain; }

.e-rail-nav {
  margin-top: var(--nav-top-mt);
  flex: 1; min-height: 0; overflow-y: auto;
  display: flex; flex-direction: column; gap: 0.125rem;
}
/* Measured on the member app's rail Sep 9 2026: 11px/600 at line-height 1.5
   (16.5px, so the label box is 46px with its padding), 0.15em tracking, at
   40% white. Ours had been 1.3 and 48%, which put every item under a group
   label 2.2px higher than the member app's and the labels a shade brighter. */
.e-rail-grouplabel {
  padding: var(--nav-group-pt) 0.875rem 0.5rem;
  font-size: var(--t-label); font-weight: 600; line-height: 1.5;
  letter-spacing: 0.15em; text-transform: uppercase;
  color: rgba(255,255,255,0.4);
}
.e-rail-item {
  display: flex; align-items: center; gap: 0.75rem;
  height: var(--nav-item-h); padding-inline: 0.875rem;
  border-radius: 0.625rem;
  font-size: var(--nav-item-fs); font-weight: 500; line-height: 1.5;
  color: rgba(255,255,255,0.75); text-decoration: none; /* the member app's 75% */
  transition: background-color var(--dur-1) var(--ease-move),
              color var(--dur-1) var(--ease-move);
}
/* Hover measured on platform /admin Sep 9 2026: a 7% white ground, white
   text, no underline, no shadow. The selector carries .e-shell so it beats
   base.css's [data-surface="enterprise"] a:hover underline. */
/* Not the active item (Thomas, Sep 9 2026, afternoon): the current page's
   item keeps its gradient under the pointer and changes nothing. */
/* Every item, active included, stays un-underlined under the pointer: the
   round 12 rule carried that only on the inactive ones, and base.css's
   a:hover underline showed through on the current page (Thomas, Sep 9). */
.e-shell .e-rail-item:hover { text-decoration: none; }
.e-shell .e-rail-item:not(.is-active):hover { background: rgba(255,255,255,0.07); color: #fff;
                              text-decoration: none; }
.e-rail-item.is-active {
  background: linear-gradient(135deg, #E0443F, #D4322F);
  color: #fff; font-weight: 600; box-shadow: var(--e-nav);
}
.e-rail-item.is-planned { color: var(--fg-on-dark-faint); cursor: default; }
.e-shell .e-rail-item.is-planned:hover { background: transparent;
                                         color: var(--fg-on-dark-faint); }
.e-rail-icon { width: 1.25rem; height: 1.25rem; flex: none; }
.e-rail-planned {
  margin-left: auto; font-size: 10px; font-weight: 700; letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--fg-on-dark-faint);
  border: 1px solid rgba(255,255,255,0.18); border-radius: var(--r-pill);
  padding: 2px 7px;
}

/* ---- Seat meter --------------------------------------------------------- */
/* Smaller since Sep 17 2026 (Thomas): the meter is a glance, not a panel, so
   the track is a hairline and the block sits closer together. The Capacity
   label and the figure beside it keep their sizes: what shrank is the space,
   not the reading. */
.e-seat {
  margin-top: var(--sp-2); padding: 0.625rem 0.875rem;
  border-top: 1px solid rgba(255,255,255,0.09);
}
.e-seat-head { display: flex; align-items: baseline;
               justify-content: space-between; gap: var(--sp-2); }
.e-seat-head .e-label { color: var(--fg-on-dark-faint); }
.e-seat-figure {
  font-family: var(--font-heading); font-size: var(--t-ui); font-weight: 700;
  color: #fff; font-variant-numeric: tabular-nums;
}
.e-seat-track {
  margin-top: 6px; height: 4px; border-radius: 2px;
  background: rgba(255,255,255,0.14); overflow: hidden;
}
.e-seat-track > i { display: block; height: 100%; border-radius: 2px;
                    background: rgba(255,255,255,0.9); }
.e-seat-track.is-warn > i { background: var(--warn-fill); }
.e-seat-track.is-crit > i { background: var(--crit-fill); }
.e-seat-note { margin-top: 6px; font-size: var(--t-meta); line-height: 1.4;
               color: var(--fg-on-dark-faint); }

/* ---- Rail footer -------------------------------------------------------- */
.e-rail-switch {
  margin: 0 0.875rem 0.75rem;
  display: grid;
  gap: 0.375rem;
}
.e-rail-switch-label {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
}
.e-rail-switch-select {
  width: 100%;
  min-height: 34px;
  padding: 0 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  font: inherit;
  font-size: 13px;
}
.e-rail-switch-select:disabled { opacity: 0.6; }
.e-rail-switch-note {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.7);
}

.e-rail-foot {
  margin-top: 0.5rem; padding: 0.75rem 0.875rem 0;
  border-top: 1px solid rgba(255,255,255,0.09);
  display: flex; align-items: center; gap: 0.625rem;
}
.e-rail-avatar {
  width: 38px; height: 38px; flex: none; border-radius: 50%;
  display: grid; place-items: center;
  background: linear-gradient(135deg, #C7566B, #8A2740); color: #fff;
  font-family: var(--font-heading); font-size: var(--t-meta); font-weight: 700;
  letter-spacing: 0.02em;
}
.e-rail-who { min-width: 0; flex: 1; }
.e-rail-signout {
  flex: none; width: 30px; height: 30px; display: grid; place-items: center;
  border-radius: 0.5rem; color: rgba(255,255,255,0.55); text-decoration: none;
  transition: background-color var(--dur-1) var(--ease-move), color var(--dur-1) var(--ease-move);
}
.e-shell .e-rail-signout:hover { background: rgba(255,255,255,0.07); color: #fff; text-decoration: none; }
.e-rail-signout-glyph { width: 18px; height: 18px; }
/* The member app's footer, measured Sep 9 2026: the name 13.5px/600 on a
   1.25 line, the line under it 11.25px/400 on a 15px line at 50% white, the
   pair centred against the 38px avatar. */
.e-rail-org {
  font-size: 13.5px; font-weight: 600; line-height: 1.25; color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.e-rail-role { font-size: 11.25px; line-height: 15px; color: rgba(255,255,255,0.5); }

/* ---- Backdrop ----------------------------------------------------------- */
.e-backdrop {
  position: fixed; inset: 0; z-index: var(--z-backdrop);
  background: rgba(0,0,0,0.4); border: 0; padding: 0; cursor: default;
}
@media (min-width: 1024px) { .e-backdrop { display: none; } }

/* ---- Frame and header ----------------------------------------------------
   The member app's header, measured Sep 9 2026: 4rem tall plus a 1px
   transparent border (61px at the 15px root), padding 0 26px from 768px and
   0 1rem below, the pill and the button 0.75rem apart on the right. It spans
   the whole column beside the rail, not the 1520px content cap. */
.e-frame { min-width: 0; }
@media (min-width: 1024px) { .e-frame { margin-left: var(--rail-w); } }

/* Transparent at every scroll position, like the member app's: measured
   Sep 9 2026 at scrollY 600 on platform /admin, the header has no
   background, no blur and no rule, and the content scrolls under the pill
   and the button. The Data Matrix's frost-on-scroll was removed at Thomas's
   direction the same day. */
.e-topbar {
  position: sticky; top: 0; z-index: var(--z-sticky);
  background: transparent;
  border-bottom: 1px solid transparent;
}
.e-topbar-inner {
  height: 4rem;
  display: flex; align-items: center; gap: 0.75rem;
  padding: 0 1rem;
}
@media (min-width: 768px) { .e-topbar-inner { padding: 0 26px; } }
.e-topbar-right {
  margin-left: auto; display: flex; align-items: center; gap: 0.75rem;
}

/* The member app's Sample Data pill: #FFD54A on #7A5A00, 10.5px bold,
   5px by 0.75rem. Two hexes, cited: they are the member app's, not tokens. */
.e-sample-pill {
  display: inline-flex; align-items: center;
  padding: 5px 0.75rem; border-radius: var(--r-pill);
  background: #FFD54A; color: #7A5A00;
  font-size: 10.5px; font-weight: 700; line-height: 1.5; letter-spacing: 0.02em;
  white-space: nowrap;
}

/* The member app's Export PDF: 12.5px semibold, 0.5rem by 1rem, a hairline
   and the card shadow, a 1rem download glyph 0.375rem before the label. */
.e-export-btn {
  appearance: none; display: inline-flex; align-items: center; gap: 0.375rem;
  padding: 0.5rem 1rem;
  background: var(--surface); color: var(--fg-body);
  border: 1px solid var(--hairline-strong); border-radius: var(--r-btn);
  box-shadow: var(--e-1);
  font-family: var(--font-sans); font-size: 12.5px; font-weight: 600;
  line-height: 1.5; cursor: pointer; white-space: nowrap;
  transition: background-color var(--dur-1) var(--ease-move);
}
.e-export-btn:hover { background: var(--p3-cream); }
.dark .e-export-btn:hover { background: var(--surface-2); }
.e-export-btn svg { width: 1rem; height: 1rem; flex: none; }
.e-export-btn:disabled { cursor: default; }

/* ---- Recompute ------------------------------------------------------------
   Three states on one button, because the pull takes a few seconds and a
   button that goes quiet for four of them reads as broken. The arrow turns
   while the Worker reads, a check lands when the figures did, and the label
   counts the cooldown down against the Worker's own clock rather than an
   estimate of it. The global prefers-reduced-motion block stops the turning. */
.e-recompute { position: relative; display: inline-flex; }
.e-export-btn.is-busy { color: var(--fg-muted); }
.e-export-btn.is-busy svg { animation: e-recompute-spin 900ms linear infinite; }
.e-export-btn.is-done { color: var(--good-text); border-color: var(--good-fill); }
.e-export-btn.is-partial { color: var(--warn-text); border-color: var(--warn-fill); }
.e-export-btn.is-done svg { animation: e-recompute-land var(--dur-2, 220ms) var(--ease-move) 1; }
.e-export-btn.is-waiting { color: var(--fg-faint); font-variant-numeric: tabular-nums; }
@keyframes e-recompute-spin { to { transform: rotate(360deg); } }
@keyframes e-recompute-land {
  from { transform: scale(0.6); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}
.e-recompute-problem {
  position: absolute; top: calc(100% + 0.5rem); right: 0; z-index: var(--z-pop, 40);
  width: max-content; max-width: 20rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface); color: var(--fg-body);
  border: 1px solid var(--hairline-strong); border-radius: var(--r-inner);
  box-shadow: var(--e-2);
  font-size: var(--t-meta); line-height: 1.45; text-align: left;
}
.e-recompute-problem.is-partial { border-color: var(--warn-fill); }

.e-iconbtn {
  appearance: none; display: inline-flex; align-items: center; gap: var(--sp-2);
  height: 34px; padding-inline: 9px;
  background: var(--surface); color: var(--fg-body);
  border: 1px solid var(--hairline-strong); border-radius: var(--r-btn);
  cursor: pointer;
  transition: background-color var(--dur-1) var(--ease-move);
}
.e-iconbtn:hover { background: var(--surface-2); }
.e-glyph { width: 17px; height: 17px; flex: none; }
.e-kbd {
  font-family: var(--font-sans); font-size: 10.5px; font-weight: 600;
  line-height: 1; letter-spacing: 0.02em;
  color: var(--fg-muted); background: var(--surface-2);
  border: 1px solid var(--hairline-strong); border-radius: 5px;
  padding: 3px 5px;
}

.e-only-narrow { display: none; }
@media (max-width: 1023px) { .e-only-narrow { display: inline-flex; } }

/* ---- Main -----------------------------------------------------------------
   The member app's column: max-w-[1520px] centred beside the rail, padding
   10px 1rem 60px, 1.75rem at the sides from 768px. */
.e-main {
  max-width: 1520px; margin-inline: auto;
  padding: 10px 1rem 60px;
}
@media (min-width: 768px) { .e-main { padding: 10px 1.75rem 60px; } }

/* ---- Hero ------------------------------------------------------------------
   The member app's band, measured at 1431x782: 18px radius, 2rem by 2.25rem
   padding, 1.25rem below; the 29px heading at 1.5 line-height; the 14.5px sub
   at 1.55 nine pixels under it; the figure grid 22px under that with 18px
   gaps; each cell 0.5rem by 1.25rem with a hairline on its left; label 10.5px
   with 0.5rem under it, figure 30px on a 1.0 line, note 11.5px 7px under. */
.e-hero {
  position: relative; overflow: hidden;
  border-radius: 18px; padding: 2rem 2.25rem; margin-bottom: 1.25rem;
  color: #fff;
  background: linear-gradient(112deg, #360B17 0%, #4A1020 55%, #280D15 100%);
}
.e-hero::after {
  content: ""; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(circle at 80% 28%, rgba(217,58,58,0.5), transparent 56%);
}
.e-hero > * { position: relative; z-index: 1; }
.e-hero-h1 {
  margin: 0; font-family: var(--font-heading);
  font-size: 29px; font-weight: 700; line-height: 1.5; letter-spacing: -0.02em;
  color: #fff;
}
.e-hero-accent { color: var(--p3-crimson); }
.e-hero-sub {
  margin: 9px 0 0; max-width: 820px;
  font-size: 14.5px; line-height: 1.55; color: rgba(255,255,255,0.74);
}
.e-hero-asof { margin: 8px 0 0; font-size: 12px; line-height: 1.5;
               color: rgba(255,255,255,0.55); }
.e-hero-kpis {
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px; margin-top: 22px;
}
@media (min-width: 1024px) {
  .e-hero-kpis { grid-template-columns: repeat(4, minmax(0, 1fr)); }
}
.e-hero-kpi { padding: 0.5rem 0 0.5rem 1.25rem; min-width: 0; }
.e-hero-kpi + .e-hero-kpi { border-left: 1px solid rgba(255,255,255,0.14); }
.e-hero-kpi-label {
  margin: 0 0 0.5rem; font-size: 10.5px; font-weight: 600; line-height: 1.5;
  letter-spacing: 0.08em; text-transform: uppercase; color: rgba(255,255,255,0.66);
}
.e-hero-kpi-value {
  margin: 0; font-family: var(--font-heading);
  font-size: 30px; font-weight: 700; line-height: 1; letter-spacing: -0.02em;
  color: #fff; font-variant-numeric: tabular-nums;
}
.e-hero-kpi-note {
  margin: 7px 0 0; font-size: 11.5px; font-weight: 500; line-height: 1.5;
  color: rgba(255,255,255,0.6);
}
/* A withheld headline reads on the maroon the way it reads on a card. */
.e-hero .e-cell-suppressed,
.e-hero .e-cell-unavailable {
  color: rgba(255,255,255,0.72); border-bottom-color: rgba(255,255,255,0.4);
  font-size: var(--t-h2);
}
.e-hero .e-trace { margin-bottom: -1px; }
.e-hero .e-trace-glyph { color: rgba(255,255,255,0.6); }
.e-hero .e-trace:hover { border-bottom-color: rgba(255,255,255,0.4); }
@media (max-width: 600px) {
  .e-hero { padding: 1.5rem 1.25rem; }
  .e-hero-h1 { font-size: 25px; }
  .e-hero-kpis { gap: 1rem 0; margin-top: 1rem; }
  .e-hero-kpi { padding: 0.25rem 0 0.25rem 1rem; }
  .e-hero-kpi:nth-child(odd) { border-left: 0; padding-left: 0; }
  .e-hero-kpi-value { font-size: 26px; }
}

/* ---- Filter bar ------------------------------------------------------------
   The member app's filter card: 13px by 16px padding, 1.25rem below, a
   10px column gap, an 11px FILTERS label, 12.5px selects at 118px minimum
   on the inset ground, and the 11px refresh stamp on the right. Its edge is
   the card's (--hairline-card, Sep 18 2026): it is a white box on the same
   canvas, and with the rest shadow now a 2px contact it needs the line. */
.e-filterbar {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 0.5rem 0.625rem; padding: 13px 16px; margin-bottom: 1.25rem;
  background: var(--surface); border: 1px solid var(--hairline-card);
  border-radius: var(--r-card); box-shadow: var(--e-card);
}
.e-filterbar-label {
  font-size: 11px; font-weight: 600; line-height: 1.5; letter-spacing: 0.1em;
  text-transform: uppercase; color: var(--fg-muted); margin-right: 0.25rem;
}
/* A select takes the width of its LONGEST option, so the comparison control
   would size to "Compare with all P3 students" and push the row to two lines
   at 1431. Capped; the browser truncates a long selected label with an
   ellipsis, and the short defaults never reach the cap. */
.e-filterbar .e-select {
  width: auto; min-width: 118px; max-width: 13rem; min-height: 0;
  padding: 0.5rem 2rem 0.5rem 0.75rem;
  font-size: 12.5px; font-weight: 500; line-height: 1.5;
  border-radius: 10px; background-color: var(--surface-2);
}
.e-filterbar .e-select:hover { border-color: var(--accent); }
.e-filterbar-note { font-size: var(--t-meta); color: var(--fg-muted); }
.e-filterbar + .e-sec { margin-top: var(--sp-5); }
/* The pricing action in the sample's top bar: the export button's height, so
   the cluster does not change shape between hosts. */
.e-pricing-cta { min-height: 36px; padding-inline: 1rem; white-space: nowrap; }
/* The fold (Sep 18 2026). Above 600px the body is display: contents, so its
   children lay out in the bar's own flex row exactly as before and the fold
   button does not exist. Below, the bar is the button until it is pressed. */
.e-filterbar-body { display: contents; }
.e-filterbar-fold { display: none; }
@media (max-width: 600px) {
  .e-filterbar { padding: 0; }
  .e-filterbar-fold {
    display: flex; align-items: center; justify-content: space-between;
    width: 100%; padding: 13px 16px; background: none; border: 0;
    cursor: pointer; font: inherit; color: inherit; text-align: left;
  }
  .e-filterbar-fold .e-filterbar-label { margin: 0; }
  .e-filterbar-fold-glyph { color: var(--fg-faint); font-size: 12px; }
  .e-filterbar-body { display: none; }
  .e-filterbar.is-open .e-filterbar-body {
    display: flex; flex-direction: column; gap: 0.5rem;
    padding: 0 16px 13px; border-top: 1px solid var(--hairline);
    padding-top: 12px;
  }
  .e-filterbar-body > .e-filterbar-label { display: none; }
  .e-filterbar .e-select { width: 100%; max-width: none; }
}
/* The hero at day one (Sep 18 2026): real zeros, drawn in the withheld tone
   until the floor is crossed, so the page's weight moves to the meter below. */
.e-hero.is-building .e-hero-kpi-value { color: rgba(255,255,255,0.55); }
.e-hero.is-building .e-hero-kpi-note { color: rgba(255,255,255,0.45); }

/* ---- Command palette ---------------------------------------------------- */
.e-cmdk-layer { position: fixed; inset: 0; z-index: var(--z-dialog); }
.e-cmdk-backdrop {
  position: absolute; inset: 0; background: rgba(0,0,0,0.42);
  border: 0; padding: 0; cursor: default;
}
.e-cmdk {
  position: absolute; left: 50%; top: 12vh; transform: translateX(-50%);
  width: min(640px, calc(100vw - var(--sp-8)));
  max-height: 70vh; display: flex; flex-direction: column;
  background: var(--surface-raised); color: var(--fg-body);
  border: 1px solid var(--hairline-strong); border-radius: var(--r-card);
  box-shadow: var(--e-3); overflow: hidden;
  animation: e-cmdk-in var(--dur-2) var(--ease-out) both;
}
@keyframes e-cmdk-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}
.e-cmdk-search {
  display: flex; align-items: center; gap: var(--sp-3);
  padding: var(--sp-4) var(--sp-4);
  border-bottom: 1px solid var(--hairline);
}
.e-cmdk-search-icon { width: 17px; height: 17px; flex: none; color: var(--fg-faint); }
.e-cmdk-input {
  flex: 1; min-width: 0; border: 0; background: transparent; color: var(--fg);
  font-size: var(--t-body); font-weight: 500; outline: none;
}
.e-cmdk-input::placeholder { color: var(--fg-faint); font-weight: 400; }
.e-cmdk-list { overflow-y: auto; padding: var(--sp-2); }
.e-cmdk-group { padding: var(--sp-3) var(--sp-3) var(--sp-1); }
.e-cmdk-item {
  appearance: none; width: 100%; border: 0; background: transparent;
  display: flex; align-items: center; gap: var(--sp-3);
  padding: 9px var(--sp-3); border-radius: var(--r-inner);
  text-align: left; cursor: pointer; color: var(--fg-body);
  font-size: var(--t-ui);
}
.e-cmdk-item.is-cursor { background: var(--accent-quiet); color: var(--fg); }
.e-cmdk-label { display: flex; align-items: center; gap: var(--sp-2);
                min-width: 0; font-weight: 500; }
.e-cmdk-tick { width: 12px; flex: none; color: var(--accent-text);
               font-size: 12px; line-height: 1; }
.e-cmdk-hint {
  margin-left: auto; padding-left: var(--sp-4);
  font-size: var(--t-meta); color: var(--fg-muted); text-align: right;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.e-cmdk-empty { padding: var(--sp-6); font-size: var(--t-ui);
                color: var(--fg-muted); text-align: center; }
.e-cmdk-foot {
  display: flex; flex-wrap: wrap; gap: var(--sp-2) var(--sp-4);
  align-items: center;
  padding: var(--sp-3) var(--sp-4);
  border-top: 1px solid var(--hairline);
  font-size: var(--t-meta); color: var(--fg-faint);
}
.e-cmdk-foot .e-kbd { margin-right: 3px; }
@media (max-width: 600px) { .e-cmdk { top: 4vh; max-height: 86vh; } }

/* ==========================================================================
   Panel hooks

   The charts and the workflow cards draw into these. They live here so the
   surface has one supplementary stylesheet, and every one of them is defined
   in tokens.
   ========================================================================== */

/* ---- The screen-reader table fallback, contained ------------------------
   Every chart ships a hidden data table, and a table element cannot be laid
   out narrower than its own content: a 1px width and a clip rectangle hide it
   visually but leave a 600px layout box behind, which gave the whole page a
   horizontal scrollbar on a phone. The fix is to take the table out of
   absolute positioning and put a 1 by 1 clipping wrapper around it. The
   content stays in the accessibility tree exactly as it was. */
[data-surface="enterprise"] .e-a11y-clip {
  position: relative; width: 1px; height: 1px; overflow: hidden;
}
[data-surface="enterprise"] .e-a11y-clip > table.e-sr-only {
  position: static; clip: auto; width: auto; height: auto; margin: 0;
}

/* ---- Section rhythm ------------------------------------------------------
   base.css owns the card's internal owl rules and the grid; a section's own
   blocks are spaced here, so no panel carries an inline margin. */
.e-sec > .e-kpi-row + *,
.e-sec > .e-grid + *,
.e-sec > .e-claimgrid + *,
.e-sec > .e-card + * { margin-top: var(--gap); }
.e-sec > .e-cap { margin-top: var(--sp-8); margin-bottom: var(--sp-3); }
.e-sec > .e-insight { margin-top: var(--sp-6); }
/* The rail scrolls to the heading, so the heading carries the offset. */
.e-shead h2 { scroll-margin-top: calc(var(--top-h) + var(--sp-4)); }
.e-shead h2:focus { outline: none; }

/* ---- Plot shell --------------------------------------------------------- */
.e-plot { position: relative; }

/* ---- Trend -------------------------------------------------------------- */
.e-trend { position: relative; display: grid;
           grid-template-columns: 34px minmax(0, 1fr); }
.e-trend-y { position: relative; height: var(--plot-h); }
.e-trend-y > span {
  position: absolute; right: var(--sp-2); transform: translateY(-50%);
  font-size: var(--t-axis); font-weight: 500; color: var(--chart-axis);
  font-variant-numeric: tabular-nums;
}
.e-trend-plot { position: relative; height: var(--plot-h); }
.e-trend-plot > svg { display: block; width: 100%; height: 100%; }
.e-trend-x { position: relative; height: 18px; grid-column: 2; }
.e-trend-x > span {
  position: absolute; transform: translateX(-50%); top: 3px;
  font-size: var(--t-axis); font-weight: 500; color: var(--chart-axis);
  white-space: nowrap;
}
.e-anno-labels { position: relative; height: 20px; grid-column: 2; }
.e-anno-labels > span {
  position: absolute; transform: translateX(-50%); top: 0;
  font-size: 10.5px; font-weight: 600; letter-spacing: 0.02em;
  color: var(--fg-muted); background: var(--surface-2);
  border: 1px solid var(--hairline); border-radius: var(--r-pill);
  padding: 1px 7px; white-space: nowrap;
}

/* ---- Segmented bar ------------------------------------------------------ */
.e-segbar {
  display: flex; height: 14px; border-radius: 7px; overflow: hidden;
  background: var(--chart-track);
}
.e-segbar > i { display: block; height: 100%; }
.e-segkey { display: flex; flex-wrap: wrap; gap: var(--sp-2) var(--sp-4);
            margin-top: var(--sp-3); }
.e-segkey > li { display: flex; align-items: center; gap: var(--sp-2);
                 font-size: var(--t-meta); color: var(--fg-body); }
.e-segkey b { width: 9px; height: 9px; border-radius: 3px; flex: none; }
.e-segkey span { font-variant-numeric: tabular-nums; color: var(--fg-muted); }

/* ---- Paired rows -------------------------------------------------------- */
.e-paired > li + li { margin-top: var(--sp-4); }
.e-paired-head { display: flex; align-items: baseline;
                 justify-content: space-between; gap: var(--sp-3);
                 margin-bottom: var(--sp-2); }
.e-paired-name { font-size: var(--t-ui); font-weight: 600; color: var(--fg); }
.e-paired-sub { font-size: var(--t-meta); color: var(--fg-muted); }

/* ---- Claim centre ------------------------------------------------------- */
.e-claimgrid {
  display: grid; grid-template-columns: 1fr; gap: var(--gap);
  align-items: start;
}
@media (min-width: 901px) {
  /* One column. It went to one on Sep 9 2026 when the "Put it in front of
     students" advice card was cut, and it stays at one now that the
     invitation card has taken that slot (Sep 12 2026): the message is meant
     to be read before it is sent, and a full-width block wraps a sentence
     where a half-width one breaks it. Both cards span the row. */
  .e-claimgrid { grid-template-columns: minmax(0, 1fr); }
}
.e-copyrow {
  display: flex; align-items: center; gap: var(--sp-2);
  border: 1px solid var(--hairline-strong); border-radius: var(--r-input);
  background: var(--surface-2); padding: var(--sp-2) var(--sp-2) var(--sp-2) var(--sp-3);
}
.e-copyrow code {
  flex: 1; min-width: 0; overflow-x: auto; white-space: nowrap;
  font-family: var(--font-sans); font-size: var(--t-ui); font-weight: 600;
  color: var(--fg);
}
.e-joincode {
  font-family: var(--font-heading); font-size: 2rem; font-weight: 700;
  letter-spacing: 0.16em; color: var(--fg); line-height: 1;
  font-variant-numeric: tabular-nums;
}
.e-qr { width: 132px; height: 132px; flex: none; border-radius: var(--r-inner);
        background: #fff; padding: 8px; border: 1px solid var(--hairline-strong); }
.e-qr svg { display: block; width: 100%; height: 100%; }
/* The invitation's message block (ClaimCentre). Survived the Sep 9 2026 cut
   of the paste block it was written for, and is in use again since Sep 12. */
.e-paste {
  margin: 0; padding: var(--sp-3); border-radius: var(--r-inner);
  background: var(--surface-2); border: 1px solid var(--hairline);
  font-family: var(--font-sans); font-size: var(--t-meta); line-height: 1.6;
  color: var(--fg-body); white-space: pre-wrap; max-height: 15rem;
  overflow-y: auto;
}
.e-steps { counter-reset: step; }
.e-steps > li {
  position: relative; padding-left: var(--sp-8);
  font-size: var(--t-ui); line-height: 1.55; color: var(--fg-body);
}
.e-steps > li + li { margin-top: var(--sp-3); }
.e-steps > li::before {
  counter-increment: step; content: counter(step);
  position: absolute; left: 0; top: 0;
  width: 22px; height: 22px; border-radius: 50%;
  display: grid; place-items: center;
  background: var(--accent-quiet); color: var(--accent-text);
  font-family: var(--font-heading); font-size: 11px; font-weight: 700;
}

/* ---- Definition rows ---------------------------------------------------- */
.e-defs > div { display: flex; align-items: baseline;
                justify-content: space-between; gap: var(--sp-4);
                padding: var(--sp-2) 0; }
.e-defs > div + div { border-top: 1px solid var(--hairline); }
/* The last row of a plan or an account is usually its point (the seats used,
   the access level), and it reads better with a little air under it. */
.e-defs > div:last-child { padding-bottom: 0; }
/* The label is the quiet half of the row (Sep 17 2026). Label and value sat
   in the same colour and the same face, which is why a column of these read
   as a printed form rather than a specification: the eye had nothing to scan
   down. The label steps back to --fg-muted; the value comes forward onto the
   heading face. Neither changes SIZE, which is the rule below. */
.e-defs dt { font-size: var(--t-ui); color: var(--fg-muted); min-width: 0; }
/* One size for every value, whatever it wears inside (Sep 17 2026, Thomas:
   the Billing card's price and claimed count sat at 15px beside 13px rows,
   because .e-num and a CellValue carry no size of their own and took the
   card's). The label is the ui step at 400, the value the ui step at 500. */
.e-defs dd { margin: 0; font-variant-numeric: tabular-nums; flex: none;
             font-family: var(--font-heading); color: var(--fg);
             font-size: var(--t-ui); font-weight: 600; line-height: 1.45;
             letter-spacing: -0.006em; }
/* The denominator after a count ("of 300"): the same size, quieter. */
.e-defs .e-defs-of { color: var(--fg-faint); font-weight: 400; }

/* ---- Absence list ------------------------------------------------------- */
.e-absent > li + li { margin-top: var(--sp-4);
                      padding-top: var(--sp-4);
                      border-top: 1px solid var(--hairline); }
.e-absent-head { display: flex; align-items: baseline; gap: var(--sp-3);
                 justify-content: space-between; margin-bottom: var(--sp-2); }
.e-absent-why { font-size: var(--t-ui); line-height: 1.55; color: var(--fg-body); }

/* ---- KPI tile hues -------------------------------------------------------
   The member app's four tile colours (admin.route.tsx ACCENT_ICON): crimson,
   plum, green, amber. A hue marks the card and encodes nothing; the tone
   classes in base.css still carry a direction when there is one. */
.e-kpi-tile.hue-1 { background: var(--accent-quiet); color: var(--accent); }
.e-kpi-tile.hue-2 { background: color-mix(in srgb, var(--cat-3) 13%, var(--surface)); color: var(--cat-3); }
.e-kpi-tile.hue-3 { background: var(--good-bg); color: var(--good-fill); }
.e-kpi-tile.hue-4 { background: var(--warn-bg); color: var(--warn-fill); }
.e-kpi-tile > svg { width: 16px; height: 16px; }

/* ---- Utility ------------------------------------------------------------ */
.e-stack > * + * { margin-top: var(--sp-3); }
.e-row { display: flex; align-items: center; gap: var(--sp-2); flex-wrap: wrap; }
.e-minor-row {
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--sp-4);
}
@media (max-width: 600px) { .e-minor-row { grid-template-columns: 1fr 1fr; } }

/* ---- Print ---------------------------------------------------------------
   The photograph, the filter bar and the header's controls are screen
   furniture. The hero prints as the band it is, on the cited colours,
   because base.css already forces print-color-adjust exact. */
@media print {
  .e-shell .e-watermark, .e-filterbar, .e-sample-pill, .e-export-btn {
    display: none !important;
  }
  .e-hero { break-inside: avoid; margin-bottom: var(--sp-4); }
}
`;
